import { useEffect, useState } from "react";
import { api } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Loader2, ShieldAlert, UserCog, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { useAuth } from "../auth";

const ROLES = ["Admin", "Management", "HR"];
const ENTITIES = ["UAFPL", "UAPL", "UOSPL"];

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [data, setData] = useState(empty());
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        const payload = { ...data };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", data);
        toast.success("User created — welcome email sent");
      }
      setOpen(false);
      setEditing(null);
      setData(empty());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const startEdit = (u) => {
    setEditing(u);
    setData({ ...u, password: "" });
    setOpen(true);
  };

  const startCreate = () => {
    setEditing(null);
    setData(empty());
    setOpen(true);
  };

  const toggleStatus = async (u) => {
    try {
      const next = u.status === "Active" ? "Inactive" : "Active";
      await api.put(`/users/${u.id}`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const onDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Admin</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage employees, roles and access.</p>
        </div>
        <Button onClick={startCreate} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Member
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 text-left text-[11px] uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Entity</th>
              <th className="px-4 py-3">Manager</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border hover:bg-muted/30">
                <td className="px-4 py-3 font-medium">{u.employee_name}</td>
                <td className="px-4 py-3 text-foreground/80">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3 text-foreground/80">{u.entity}</td>
                <td className="px-4 py-3 text-foreground/80">{u.manager_name || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={u.status === "Active"} onCheckedChange={() => toggleStatus(u)} data-testid={`toggle-${u.id}`} />
                    <span className={`text-xs ${u.status === "Active" ? "text-emerald-700" : "text-muted-foreground"}`}>{u.status}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  {u.id !== me?.id && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete user "{u.employee_name}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently removes the user account. The user will no longer be able to sign in.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDelete(u)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">
                <UserCog className="w-8 h-8 mx-auto opacity-40 mb-2" />
                No users yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit Member" : "Add Member"}</DialogTitle>
            <DialogDescription>{editing ? "Update employee details and access." : "Send a welcome email with login credentials."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="user-form">
            <Field label="Employee Name *">
              <Input required value={data.employee_name} onChange={(e) => setData({ ...data, employee_name: e.target.value })} data-testid="user-name" />
            </Field>
            <Field label="Email *">
              <Input type="email" required value={data.email} disabled={!!editing} onChange={(e) => setData({ ...data, email: e.target.value })} data-testid="user-email" />
            </Field>
            <Field label="Role *">
              <Select value={data.role} onValueChange={(v) => setData({ ...data, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Entity *">
              <Select value={data.entity} onValueChange={(v) => setData({ ...data, entity: v })}>
                <SelectTrigger data-testid="user-entity"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Manager Name">
              <Input value={data.manager_name} onChange={(e) => setData({ ...data, manager_name: e.target.value })} />
            </Field>
            <Field label="Manager Email">
              <Input type="email" value={data.manager_email} onChange={(e) => setData({ ...data, manager_email: e.target.value })} />
            </Field>
            <Field label={editing ? "New Password (leave blank to keep)" : "Password *"} full>
              <Input type="password" required={!editing} minLength={6} value={data.password} onChange={(e) => setData({ ...data, password: e.target.value })} data-testid="user-password" />
            </Field>

            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="user-submit">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editing ? "Save changes" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function empty() {
  return { employee_name: "", email: "", role: "HR", entity: "UAFPL", manager_name: "", manager_email: "", status: "Active", password: "" };
}
function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="text-xs text-muted-foreground mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
